import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type ComponentRef,
} from "react";

import { Command } from "cmdk";

import { ChevronDownIcon, MagnifyingGlassIcon } from "@eisox/icons";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import { createContext } from "@radix-ui/react-context";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import * as Control from "../Control/control.component";
import { cn } from "../index";
import { Popover } from "../Popover";
import { Radio } from "../Radio";
import { TextField } from "../TextField";
import { Typography } from "../Typography";

/* -------------------------------------------------------------------------------------------------
 * Select
 * -----------------------------------------------------------------------------------------------*/

const SELECT_NAME = "select";

interface SelectContextValue<T extends boolean> {
  multiple: T;
  value: T extends true ? string[] : string;
  onChange: (value: T extends true ? string[] : string) => void;
  displayedValue: string | undefined;
  onDisplayedValueChange: (value: string | undefined) => void;
  open: boolean;
  onOpenChange: (value: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}

const [SelectProvider, useSelectContext] = createContext<SelectContextValue<boolean>>(SELECT_NAME);

interface SelectProps<T extends boolean> extends Popover.PopoverProps {
  multiple: T;
  value?: T extends true ? string[] : string;
  onValueChange?: (value: T extends true ? string[] : string) => void;
  defaultValue?: T extends true ? string[] : string;
  children: React.ReactNode;
}

const Root = <T extends boolean>(props: SelectProps<T>) => {
  const {
    multiple,
    value: valueProp,
    onValueChange,
    defaultValue = (multiple ? [] : "") as T extends true ? string[] : string,
    open: openProp,
    onOpenChange,
    defaultOpen = false,
    children,
  } = props;

  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen,
  });
  const [value, onChange] = useControllableState({
    prop: valueProp,
    onChange: onValueChange,
    defaultProp: defaultValue,
  });
  const [displayedValue, setDisplayedValue] = useState<string>();
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <SelectProvider
      multiple={multiple}
      value={value}
      // @ts-expect-error : type problem
      onChange={onChange}
      displayedValue={displayedValue}
      onDisplayedValueChange={setDisplayedValue}
      open={open}
      onOpenChange={setOpen}
      triggerRef={triggerRef}
    >
      <Popover.Root open={open} onOpenChange={setOpen}>
        {children}
      </Popover.Root>
    </SelectProvider>
  );
};

Root.displayName = "Select";

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

const TRIGGER_NAME = "SelectTrigger";

type TriggerProps = Omit<Popover.TriggerProps, "asChild"> & {
  error?: boolean;
};

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  ({ className, children, error, ...props }, forwardedRef) => {
    const { open, triggerRef } = useSelectContext(TRIGGER_NAME);
    const composedRefs = useComposedRefs(forwardedRef, triggerRef);

    const prevOpenState = useRef(open);

    useEffect(() => {
      if (!open && prevOpenState.current) triggerRef.current?.focus();
      prevOpenState.current = open;
    }, [open, triggerRef]);

    return (
      <Control.Root
        asChild
        error={error}
        className={cn(
          "gap-1.25 cursor-pointer items-center focus-visible:border-primary-blue disabled:cursor-default disabled:bg-primary-gray",
          className,
        )}
      >
        <Popover.Trigger
          {...props}
          ref={composedRefs}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls="select-listbox"
          aria-labelledby="select-label"
        >
          {children}
          <ChevronDownIcon className="ml-auto shrink-0" />
        </Popover.Trigger>
      </Control.Root>
    );
  },
);

Trigger.displayName = TRIGGER_NAME;

/* -------------------------------------------------------------------------------------------------
 * Value
 * -----------------------------------------------------------------------------------------------*/

const VALUE_NAME = "SelectValue";

interface ValueProps extends Omit<ComponentPropsWithRef<"p">, "children"> {
  placeholder?: string;
  children?: React.ReactNode;
}

const Value = forwardRef<HTMLParagraphElement, ValueProps>(
  ({ children, placeholder, className, ...props }, forwardedRef) => {
    const { value, displayedValue } = useSelectContext(VALUE_NAME);

    return (
      <Typography {...props} ref={forwardedRef} className={cn("text-base text-primary-dark-blue", className)}>
        {shouldShowPlaceholder(value) ? (
          <span className="font-light text-primary-dark-gray italic">{placeholder}</span>
        ) : (
          (children ?? displayedValue)
        )}
      </Typography>
    );
  },
);

Value.displayName = "SelectValue";

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

const CONTENT_NAME = "SelectContent";
interface SelectContentContextValue {
  itemRefCallback?: (node: SelectItemElement | null, value: string, disabled: boolean) => void;
  itemTextCallback?: (name: string, value: string) => void;
  input: boolean;
  inputRefCallback: (node: SelectInputElement | null) => void;
  search: string;
  onSearchChange: (value: string) => void;
}

const [SelectContentProvider, useSelectContentContext] = createContext<SelectContentContextValue>(CONTENT_NAME);

type ContentProps = Popover.ContentProps;

const Content = forwardRef<HTMLDivElement, ContentProps>(({ className, children, ...props }, forwardedRef) => {
  const { open, onOpenChange, value, onDisplayedValueChange, triggerRef } = useSelectContext(CONTENT_NAME);

  const input = useRef(false);
  const [search, setSearch] = useState("");

  const [items, setItems] = useState(() => new Map<string, { name: string; disabled: boolean }>());
  const [itemTexts, setItemTexts] = useState(() => new Map<string, string>());

  const options = useMemo(() => {
    const options = new Map(items);
    for (const [key, value] of itemTexts) {
      options.set(key, { name: value, disabled: options.get(key)?.disabled ?? false });
    }
    return options;
  }, [items, itemTexts]);
  const displayedValue = useMemo(() => {
    if (Array.isArray(value)) {
      return value.map(v => options.get(v)?.name).join(", ");
    }
    return options.get(value)?.name;
  }, [value, options]);

  useEffect(() => onDisplayedValueChange(displayedValue), [displayedValue, onDisplayedValueChange]);
  useEffect(() => () => onDisplayedValueChange(undefined), [onDisplayedValueChange]);

  const itemRefCallback = useCallback((node: SelectItemElement | null, value: string, disabled: boolean) => {
    if (!node) return;
    const textContent = node.textContent;
    setItems(prev => {
      const newOptions = new Map(prev);
      newOptions.set(value, { name: textContent, disabled });
      return newOptions;
    });
  }, []);

  const itemTextCallback = useCallback(
    (name: string, value: string) =>
      setItemTexts(prev => {
        const newOptions = new Map(prev);
        newOptions.set(value, name);
        return newOptions;
      }),
    [],
  );

  const inputRefCallback = useCallback((node: HTMLInputElement | null) => {
    input.current = node !== null;
  }, []);

  // Reset search when popover closes
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  return (
    <SelectContentProvider
      itemRefCallback={itemRefCallback}
      itemTextCallback={itemTextCallback}
      input={input.current}
      inputRefCallback={inputRefCallback}
      search={search}
      onSearchChange={setSearch}
    >
      <Popover.Content
        {...props}
        asChild
        className={cn(
          `p-2.5 gap-1.25 box-border flex max-h-[min(var(--radix-popover-content-available-height,300px),300px)] w-(--radix-popover-trigger-width) flex-col bg-primary-white text-base shadow-(--shadow-elevated) data-[open=false]:hidden`,
          className,
        )}
        sideOffset={5}
        forceMount
        onInteractOutside={event => {
          props.onInteractOutside?.(event);
          if (event.defaultPrevented) return;
          event.preventDefault();
          const isOnTrigger = event.target instanceof Node && triggerRef.current?.contains(event.target);
          if (open && !isOnTrigger) onOpenChange(false);
        }}
        onWheel={e => e.stopPropagation()}
      >
        <Command ref={forwardedRef} data-open={open} role="listbox" id="select-listbox" aria-labelledby="select-label">
          {children}
        </Command>
      </Popover.Content>
    </SelectContentProvider>
  );
});

Content.displayName = CONTENT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Viewport
 * -----------------------------------------------------------------------------------------------*/

const VIEWPORT_NAME = "SelectViewport";

type ViewportProps = ComponentPropsWithRef<"div">;

const Viewport = forwardRef<HTMLDivElement, ViewportProps>(({ children, className, ...props }, forwardedRef) => {
  const { open } = useSelectContext(VIEWPORT_NAME);
  const { input, search } = useSelectContentContext(VIEWPORT_NAME);
  const viewportRef = useRef<HTMLDivElement>(null);
  const composedRefs = useComposedRefs(viewportRef, forwardedRef);

  useEffect(() => {
    if (open && !input) viewportRef.current?.focus();
  }, [open, input]);

  useEffect(() => {
    if (!open || !viewportRef.current) return;
    if (search.length > 0) {
      viewportRef.current.scrollTop = 0;
      return;
    }
    viewportRef.current.querySelector<HTMLElement>('[data-value-selected="true"]')?.scrollIntoView({ block: "start" });
  }, [open, search]);

  return (
    <Command.List
      {...props}
      ref={composedRefs}
      className={cn("overflow-x-hidden overflow-y-auto focus:shadow-none focus:outline-none", className)}
      onTouchMove={e => e.stopPropagation()}
    >
      {children}
    </Command.List>
  );
});

Viewport.displayName = VIEWPORT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Input
 * -----------------------------------------------------------------------------------------------*/

const INPUT_NAME = "SelectInput";

type SelectInputElement = ComponentRef<typeof Input>;

interface InputProps extends ComponentPropsWithRef<"input"> {
  value?: string;
  onValueChange?: (search: string) => void;
  defaultValue?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>((props, forwardedRef) => {
  const { open } = useSelectContext(INPUT_NAME);
  const { inputRefCallback, search, onSearchChange } = useSelectContentContext(INPUT_NAME);

  const { value: valueProp, onValueChange, defaultValue: defaultValueProp = "", ...inputProps } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const composedRefs = useComposedRefs(inputRef, node => inputRefCallback(node), forwardedRef);

  const [value, setValue] = useControllableState({
    prop: valueProp,
    onChange: onValueChange,
    defaultProp: defaultValueProp,
  });

  // Sync internal value with context search state
  const handleValueChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      onSearchChange(newValue);
    },
    [setValue, onSearchChange],
  );

  // Sync context search with internal value when it changes externally (e.g., on close)
  useEffect(() => {
    if (search !== value) {
      setValue(search);
    }
    // Only sync when search changes from context (e.g., reset on close)
    // oxlint-disable-next-line react/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  return (
    <Command.Input {...inputProps} ref={composedRefs} value={value} onValueChange={handleValueChange} asChild>
      <TextField.Root>
        <TextField.Slot>
          <MagnifyingGlassIcon />
        </TextField.Slot>
      </TextField.Root>
    </Command.Input>
  );
});

Input.displayName = INPUT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Group
 * -----------------------------------------------------------------------------------------------*/

const GROUP_NAME = "SelectGroup";

interface GroupProps extends ComponentPropsWithRef<"div"> {
  heading?: React.ReactNode;
}

const Group = forwardRef<HTMLDivElement, GroupProps>(({ heading, className, ...groupProps }, forwardedRef) => (
  <Command.Group
    {...groupProps}
    ref={forwardedRef}
    className={cn(
      `group **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:p-[7.5px] **:[[cmdk-group-heading]]:text-(--font-size-base) **:[[cmdk-group-heading]]:select-none`,
      className,
    )}
    heading={heading}
  />
));

Group.displayName = GROUP_NAME;

/* -------------------------------------------------------------------------------------------------
 * Item
 * -----------------------------------------------------------------------------------------------*/

const ITEM_NAME = "SelectItem";

interface SelectItemContextValue {
  value: string;
}

type SelectItemElement = ComponentRef<typeof Item>;

const [SelectItemProvider, useSelectItemContext] = createContext<SelectItemContextValue>(ITEM_NAME);

interface ItemProps extends Omit<ComponentPropsWithRef<"div">, "value" | "onSelect"> {
  value: string;
  searchValue?: string;
  disabled?: boolean;
  onSelect?: (value: string) => void;
}

const Item = forwardRef<HTMLDivElement, ItemProps>((props, forwardedRef) => {
  const { itemRefCallback } = useSelectContentContext(ITEM_NAME);
  const { multiple, value, onChange, onOpenChange } = useSelectContext(ITEM_NAME);
  const { value: valueProp, searchValue, disabled = false, onSelect, className, children, ...itemProps } = props;

  const itemRef = useRef<HTMLDivElement>(null);
  const composedRefs = useComposedRefs(itemRef, forwardedRef, node => itemRefCallback?.(node, valueProp, disabled));

  const handleSelectItem = () => {
    if (multiple && Array.isArray(value)) {
      if (value.includes(valueProp)) {
        onChange(value.filter(v => v !== valueProp));
      } else {
        onChange([...value, valueProp]);
      }
    } else if (!multiple && typeof value === "string") {
      onChange(valueProp);
    } else {
      throw new Error("Invalid value type");
    }
    if (!multiple) onOpenChange(false);
    onSelect?.(valueProp);
  };

  const selected = multiple ? value.includes(valueProp) : value === valueProp;

  return (
    <SelectItemProvider value={valueProp}>
      <Command.Item
        {...itemProps}
        ref={composedRefs}
        value={searchValue}
        className={cn(
          `mb-0.5 font-normal gap-1.25 flex cursor-pointer items-center rounded-[5px] p-[7.5px] text-base data-[disabled=true]:text-primary-dark-gray data-[selected=true]:bg-primary-gray`,
          selected && "text-primary-blue",
          className,
        )}
        onSelect={handleSelectItem}
        disabled={disabled}
        role="option"
        aria-selected={selected}
        aria-disabled={disabled}
        data-value-selected={selected}
      >
        {multiple && <Radio multiple checked={selected} disabled={disabled} />}
        {children}
      </Command.Item>
    </SelectItemProvider>
  );
});

Item.displayName = ITEM_NAME;

/* -------------------------------------------------------------------------------------------------
 * ItemText
 * -----------------------------------------------------------------------------------------------*/

const ITEM_TEXT_NAME = "SelectItemText";

interface ItemTextProps {
  children: string;
}

const ItemText: React.FC<ItemTextProps> = ({ children }) => {
  const { itemTextCallback } = useSelectContentContext(ITEM_TEXT_NAME);
  const { value: itemValue } = useSelectItemContext(ITEM_TEXT_NAME);

  useEffect(() => itemTextCallback?.(children, itemValue), [children, itemTextCallback, itemValue]);

  return null;
};

ItemText.displayName = ITEM_TEXT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Empty
 * -----------------------------------------------------------------------------------------------*/

const EMPTY_NAME = "SelectEmpty";

type EmptyProps = ComponentPropsWithRef<"div">;

const Empty = forwardRef<HTMLDivElement, EmptyProps>(({ className, ...props }, forwardedRef) => (
  <Command.Empty
    {...props}
    ref={forwardedRef}
    className={cn("flex items-center justify-center p-[7.5px] text-base", className)}
  />
));

Empty.displayName = EMPTY_NAME;

const shouldShowPlaceholder = (value: string | string[] | undefined) => {
  if (Array.isArray(value)) return value.length === 0;
  return value === "" || value === undefined;
};

export { Content, Empty, Group, Input, Item, ItemText, Root, Trigger, Value, Viewport };
export type {
  ContentProps,
  EmptyProps,
  GroupProps,
  InputProps,
  ItemProps,
  ItemTextProps,
  SelectProps,
  TriggerProps,
  ValueProps,
  ViewportProps,
};
